import { RedoAlert } from "@redotech/arbiter/alert/redo-alert";
import { Flex } from "@redotech/arbiter/flex";
import { RedoTextInput } from "@redotech/arbiter/input/redo-text-input";
import { RedoModal } from "@redotech/arbiter/modal/redo-modal";
import KeyIcon from "@redotech/redo-icons/key-01.svg";
import { apiTokenNameSchema } from "@redotech/redo-partners-portal-rpc/schema/shared/api-token";
import { toast } from "@redotech/redo-web/alert";
import { memo, type ReactNode, useState } from "react";

import { CopyField } from "./copy-field";

/**
 * Names a new token, then shows its secret once. The secret lives only in
 * this modal's state and is gone when it closes.
 */
export const CreateTokenModal = memo(function CreateTokenModal({
  title,
  subtitle,
  canSubmit = true,
  children,
  create,
  onClose,
}: {
  title: string;
  subtitle: string;
  /** False while extra fields, such as scopes, are incomplete. */
  canSubmit?: boolean;
  children?: ReactNode;
  /** Creates the token and resolves with its secret. */
  create: (name: string) => Promise<string>;
  onClose: () => void;
}) {
  const [name, setName] = useState("");
  const [secret, setSecret] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);
  const validName = apiTokenNameSchema.safeParse(name);

  if (secret !== undefined) {
    return (
      // The secret can't be shown again, so only Done closes this step.
      <RedoModal
        disableBackdropClose
        disableEscapeClose
        hideCloseButton
        onModalCloseRequested={onClose}
        primaryButton={{ text: "Done", onClick: onClose }}
        subtitle="Copy this token now. You won't be able to see it again."
        title="Your new token"
        TitleIcon={KeyIcon}
      >
        <Flex dir="column" gap="lg">
          <CopyField ariaLabel="Token" value={secret} />
          <RedoAlert
            color="warning"
            description="Store it in your secret manager. If you lose it, revoke it and create a new one."
            size="sm"
            title="Shown only once"
          />
        </Flex>
      </RedoModal>
    );
  }

  const submit = async () => {
    if (!validName.success) {
      return;
    }
    setSubmitting(true);
    try {
      setSecret(await create(validName.data));
    } catch {
      toast("Couldn't create the token. Try again.", { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <RedoModal
      onModalCloseRequested={onClose}
      primaryButton={{
        text: "Create token",
        disabled: !validName.success || !canSubmit,
        onClick: submit,
        pending: submitting,
      }}
      secondaryButton={{ text: "Cancel", onClick: onClose }}
      subtitle={subtitle}
      title={title}
      TitleIcon={KeyIcon}
    >
      <Flex dir="column" gap="xl">
        <RedoTextInput
          autoFocus
          fullWidth
          htmlId="create-token-name"
          label="Name"
          placeholder="Production sync"
          setValue={setName}
          value={name}
        />
        {children}
      </Flex>
    </RedoModal>
  );
});
